/**
 * Small Order Surcharge (Kleine ordertoeslag)
 * Auto-adds/removes a surcharge product when the cart's product total
 * (excluding the surcharge itself) is below the threshold.
 *
 * The surcharge product lives in Shopify admin with handle SURCHARGE_HANDLE
 * and carries the surcharge amount as its price. The shipping category
 * calculator excludes it (see shipping-category-calculator.js), and the cart
 * templates render it without quantity/remove controls.
 *
 * Monsters (sample products) count toward the threshold like any other
 * product, but a cart holding *only* monsters never gets the surcharge:
 * a €25 sample should not carry a €50 fee.
 */

(function() {
  'use strict';

  // ========== CONFIGURATION ==========

  const SURCHARGE_HANDLE = 'kleine-ordertoeslag';
  /** Carts under this product total (in cents, incl. BTW) get the surcharge. */
  const THRESHOLD_CENTS = 50000;
  /** Monster/sample collection. A monster-only cart is exempt from the surcharge. */
  const MONSTER_COLLECTION_ID = '711780663683';
  const DEBUG = true;

  // Save native console.log before any widget can override it.
  const nativeLog = console.log.bind(console);

  let isSyncing = false;
  let isAdjusting = false; // our own cart mutation in flight — ignored by the fetch intercept
  let isInitialSync = true; // first pass after page load: never reload the cart page
  let variantId = null;
  let variantLookupFailedAt = 0;
  const VARIANT_RETRY_MS = 5 * 60 * 1000;
  const collectionCache = {};

  // ========== HELPER FUNCTIONS ==========

  function log(...args) {
    if (DEBUG) nativeLog('[OrderToeslag]', ...args);
  }

  async function resolveVariantId() {
    if (variantId) return variantId;
    if (Date.now() - variantLookupFailedAt < VARIANT_RETRY_MS) return null;
    try {
      const res = await fetch(`/products/${SURCHARGE_HANDLE}.js`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const product = await res.json();
      variantId = product.variants?.[0]?.id || null;
      if (!variantId) throw new Error('product has no variants');
      return variantId;
    } catch (e) {
      variantLookupFailedAt = Date.now();
      log(`Surcharge product "${SURCHARGE_HANDLE}" not available:`, e.message || e);
      return null;
    }
  }

  /** Collection ids for a cart line, mirroring the shipping calculator's lookup. */
  async function getCollections(item) {
    if (item.properties?._collection_ids) {
      return item.properties._collection_ids.split(',').map(id => id.trim());
    }
    if (item.product_collections) return item.product_collections.map(String);
    if (item.collections) return item.collections.map(String);

    if (!item.handle) return [];
    if (collectionCache[item.handle]) return collectionCache[item.handle];

    try {
      const res = await fetch(`/products/${item.handle}.js`);
      if (!res.ok) return [];
      const data = await res.json();
      const ids = (data.collections || []).map(c => c.id.toString());
      collectionCache[item.handle] = ids;
      return ids;
    } catch {
      return [];
    }
  }

  /**
   * True when every real cart line is a monster. Falls back to false on a
   * lookup failure, so an unknown cart is charged rather than silently exempt.
   */
  async function isMonsterOnlyCart(items) {
    if (!items.length) return false;
    try {
      const cols = await Promise.all(items.map(getCollections));
      return cols.every(ids => ids.includes(MONSTER_COLLECTION_ID));
    } catch {
      return false;
    }
  }

  /**
   * Re-render the cart UI in place after our own mutation.
   *
   * The cart page is a plain form with no client-side rendering, so the only
   * way to show the changed line is a reload. That is guarded twice: it runs
   * at most once per pageview, and never on the very first sync after load
   * (where the server already rendered the correct cart).
   */
  let hasReloaded = false;
  function refreshCartUI(itemCount) {
    const bubble = document.querySelector('.cart-count');
    if (bubble && typeof itemCount === 'number') bubble.textContent = itemCount;

    // Drawer: refresh without opening it — the surcharge is not a user action.
    if (window.CartDrawer && typeof window.CartDrawer.refresh === 'function') {
      try { window.CartDrawer.refresh(); } catch {}
    }

    const onCartPage = window.location.pathname.indexOf('/cart') === 0;
    if (onCartPage && !isInitialSync && !hasReloaded) {
      hasReloaded = true;
      window.location.reload();
    }
  }

  /** POST a cart mutation, flagged so our own fetch intercept ignores it. */
  async function mutateCart(url, payload) {
    isAdjusting = true;
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const cart = await res.json();
      refreshCartUI(cart.item_count);
      return cart;
    } catch (e) {
      log('Cart mutation failed:', e.message || e);
      return null;
    } finally {
      isAdjusting = false;
    }
  }

  async function sync() {
    if (isSyncing || isAdjusting) return;
    isSyncing = true;

    try {
      const res = await fetch('/cart.js');
      if (!res.ok) return;
      const cart = await res.json();

      const surchargeLine = (cart.items || []).find((i) => i.handle === SURCHARGE_HANDLE);
      const otherItems = (cart.items || []).filter((i) => i.handle !== SURCHARGE_HANDLE);
      const subtotal = otherItems.reduce((sum, i) => sum + (i.final_line_price || i.line_price || 0), 0);

      // Monsters count toward the subtotal, but a monster-only cart is exempt.
      const monsterOnly = subtotal < THRESHOLD_CENTS && await isMonsterOnlyCart(otherItems);
      const needsSurcharge = otherItems.length > 0 && subtotal < THRESHOLD_CENTS && !monsterOnly;

      if (needsSurcharge && !surchargeLine) {
        const id = await resolveVariantId();
        if (!id) return;
        log(`Subtotal €${(subtotal / 100).toFixed(2)} < €${THRESHOLD_CENTS / 100} — adding surcharge`);
        await mutateCart('/cart/add.js', { items: [{ id, quantity: 1 }] });
      } else if (!needsSurcharge && surchargeLine) {
        log(monsterOnly
          ? 'Monster-only cart — removing surcharge'
          : `Subtotal €${(subtotal / 100).toFixed(2)} — removing surcharge`);
        await mutateCart('/cart/change.js', { id: surchargeLine.key, quantity: 0 });
      } else if (surchargeLine && surchargeLine.quantity !== 1) {
        log('Pinning surcharge quantity back to 1');
        await mutateCart('/cart/change.js', { id: surchargeLine.key, quantity: 1 });
      }
    } catch (e) {
      log('Sync error:', e);
    } finally {
      isSyncing = false;
      isInitialSync = false;
    }
  }

  // ========== INITIALIZATION ==========

  function init() {
    log('Initialized');

    let timeout;
    function scheduleSync(delay = 300) {
      clearTimeout(timeout);
      timeout = setTimeout(sync, delay);
    }

    scheduleSync(150);

    // Same cart events the shipping category calculator listens for
    ['cart:updated', 'cart:add', 'product:add', 'on:cart:add', 'product:added-to-cart',
     'cart:item:removed', 'cart:remove', 'on:line-item:change'].forEach((evt) => {
      document.addEventListener(evt, () => scheduleSync());
    });
    // The drawer dispatches cart:updated on window, not document.
    window.addEventListener('cart:updated', () => scheduleSync());

    // Form submissions (no-JS cart page: "Winkelwagen bijwerken")
    document.addEventListener('submit', (e) => {
      if (e.target.action?.includes('/cart')) scheduleSync();
    });

    // Intercept fetch for cart changes we didn't make ourselves
    const origFetch = window.fetch;
    window.fetch = function(input, ...rest) {
      const url = typeof input === 'string' ? input : (input instanceof Request ? input.url : '');
      if (!isAdjusting && (url.includes('/cart/add') || url.includes('/cart/change'))) {
        const p = origFetch.call(this, input, ...rest);
        p.then(() => scheduleSync()).catch(() => {});
        return p;
      }
      return origFetch.call(this, input, ...rest);
    };

    // Tab visible again: cart may have been changed elsewhere
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') scheduleSync();
    });

    // Periodic safety net, staggered against the calculator's 10s interval
    setInterval(() => {
      if (!isSyncing && !isAdjusting) sync();
    }, 15000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
